(function () {
  class ReviewsPage {
    constructor(section) {
      this.section = section;
      this.list = section.querySelector('[data-reviews-list]');
      this.sort = section.querySelector('[data-reviews-sort]');
      this.pagination = section.querySelector('[data-reviews-pagination]');
      this.showMoreButton = section.querySelector('[data-reviews-show-more]');
      this.mobileQuery = window.matchMedia('(max-width: 767px)');
      this.onViewportChange = this.updateInitialCount.bind(this);

      this.prepareReviews();
      this.sort?.addEventListener('change', () => this.sortReviews());
      this.showMoreButton?.addEventListener('click', () => this.showMoreReviews());
      this.section.addEventListener('click', (event) => this.handleHelpfulVote(event));

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.onViewportChange);
      } else {
        this.mobileQuery.addListener(this.onViewportChange);
      }

      this.updateInitialCount();
      this.sortReviews();
    }

    prepareReviews() {
      this.reviews = Array.from(this.section.querySelectorAll('[data-review]'));
      this.reviews.forEach((review) => {
        if (review.dataset.reviewBound === 'true') return;
        review.dataset.reviewBound = 'true';
        review.open = true;
        review.querySelector('summary')?.addEventListener('click', (event) => {
          event.preventDefault();
          review.open = true;
        });
        review.addEventListener('toggle', () => {
          if (!review.open) review.open = true;
        });
      });
    }

    updateInitialCount() {
      this.visibleCount = this.getInitialCount();
      this.updatePagination();
    }

    getInitialCount() {
      if (!this.pagination) return this.reviews.length;

      const setting = this.mobileQuery.matches
        ? this.pagination.dataset.initialMobile
        : this.pagination.dataset.initialDesktop;

      return Math.min(this.reviews.length, Math.max(1, Number(setting) || this.reviews.length));
    }

    updatePagination(revealFrom) {
      this.reviews.forEach((review, index) => {
        const shouldShow = index < this.visibleCount;
        review.hidden = !shouldShow;

        if (shouldShow && Number.isFinite(revealFrom) && index >= revealFrom) {
          review.classList.remove('is-revealed');
          requestAnimationFrame(() => review.classList.add('is-revealed'));
          window.setTimeout(() => review.classList.remove('is-revealed'), 400);
        }
      });

      if (this.pagination) {
        this.pagination.hidden = this.visibleCount >= this.reviews.length;
      }
    }

    showMoreReviews() {
      if (!this.pagination) return;

      const previousCount = this.visibleCount;
      const loadCount = Math.max(1, Number(this.pagination.dataset.loadCount) || 1);
      this.visibleCount = Math.min(this.reviews.length, this.visibleCount + loadCount);
      this.updatePagination(previousCount);
    }

    sortReviews() {
      if (!this.list) return;

      const mode = this.sort?.value || 'recent';
      const sorted = [...this.reviews].sort((first, second) => {
        const firstIndex = Number(first.dataset.originalIndex) || 0;
        const secondIndex = Number(second.dataset.originalIndex) || 0;

        if (mode === 'recent') {
          const firstDate = this.getReviewTimestamp(first.dataset.reviewDate);
          const secondDate = this.getReviewTimestamp(second.dataset.reviewDate);

          if (firstDate !== null && secondDate !== null) {
            return secondDate - firstDate || firstIndex - secondIndex;
          }

          if (firstDate !== null) return -1;
          if (secondDate !== null) return 1;
        }

        if (mode === 'highest') {
          return Number(second.dataset.rating) - Number(first.dataset.rating) || firstIndex - secondIndex;
        }

        if (mode === 'lowest') {
          return Number(first.dataset.rating) - Number(second.dataset.rating) || firstIndex - secondIndex;
        }

        return firstIndex - secondIndex;
      });

      sorted.forEach((review) => this.list.appendChild(review));
      this.reviews = sorted;
      this.visibleCount = this.getInitialCount();
      this.updatePagination();
    }

    getReviewTimestamp(value) {
      if (!value) return null;

      const normalized = value.trim().toLowerCase();
      const today = new Date();
      today.setHours(12, 0, 0, 0);

      if (['today', 'h\u00f4m nay'].includes(normalized)) {
        return today.getTime();
      }

      if (['yesterday', 'h\u00f4m qua'].includes(normalized)) {
        return today.getTime() - 86400000;
      }

      const relativeMatch = normalized.match(
        /^(\d+)\s*(day|days|week|weeks|month|months|year|years|ng\u00e0y|tu\u1ea7n|th\u00e1ng|n\u0103m)(?:\s+ago|\s+tr\u01b0\u1edbc)?$/
      );

      if (relativeMatch) {
        const amount = Number(relativeMatch[1]);
        const unit = relativeMatch[2];
        const daysByUnit = {
          day: 1,
          days: 1,
          'ng\u00e0y': 1,
          week: 7,
          weeks: 7,
          'tu\u1ea7n': 7,
          month: 30,
          months: 30,
          'th\u00e1ng': 30,
          year: 365,
          years: 365,
          'n\u0103m': 365,
        };

        return today.getTime() - amount * daysByUnit[unit] * 86400000;
      }

      const dayFirstMatch = normalized.match(/^(\d{1,2})[/.](\d{1,2})[/.](\d{4})$/);
      if (dayFirstMatch) {
        const parsed = new Date(
          Number(dayFirstMatch[3]),
          Number(dayFirstMatch[2]) - 1,
          Number(dayFirstMatch[1]),
          12
        );
        return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
      }

      const parsed = Date.parse(value);
      return Number.isNaN(parsed) ? null : parsed;
    }

    handleHelpfulVote(event) {
      const button = event.target.closest('[data-helpful-button]');
      if (!button || !this.section.contains(button)) return;

      const group = button.closest('[data-helpful-group]');
      const buttons = Array.from(group.querySelectorAll('[data-helpful-button]'));
      const wasPressed = button.getAttribute('aria-pressed') === 'true';

      buttons.forEach((item) => {
        const count = item.querySelector('[data-helpful-count]');
        const isPressed = item.getAttribute('aria-pressed') === 'true';
        const isSelected = item === button;

        if (isPressed && (!isSelected || wasPressed)) {
          count.textContent = Math.max(0, Number(count.textContent) - 1);
          item.setAttribute('aria-pressed', 'false');
        }
      });

      if (!wasPressed) {
        const count = button.querySelector('[data-helpful-count]');
        count.textContent = Number(count.textContent) + 1;
        button.setAttribute('aria-pressed', 'true');
      }
    }
  }

  function initReviewsPage(root) {
    root.querySelectorAll('[data-section-type="page-reviews"]').forEach((section) => {
      if (section.dataset.reviewsInitialized === 'true') return;
      section.dataset.reviewsInitialized = 'true';
      new ReviewsPage(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initReviewsPage(document));
  } else {
    initReviewsPage(document);
  }

  document.addEventListener('shopify:section:load', (event) => initReviewsPage(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const review = event.target.closest('[data-review]');
    if (review) {
      review.hidden = false;
      review.open = true;
    }
  });
})();
